import { JsonSerializable } from '@/util/object/JsonSerializable'
import { FulfillmentType, ItemRewardType, ItemRewardValueType } from '@/cowboy/EnumTypes'
import { ItemCostAdjustment } from '@/cowboy/catalog/object/ItemCostAdjustment'

export class BrandItemResponse extends JsonSerializable {
  /** @type {number} */
  #itemID
  /** @type {string} */
  #utid
  /** @type {string} */
  #sku
  /** @type {string} */
  #name
  /** @type {string} */
  #rewardName
  /** @type {ItemRewardValueType} */
  #valueType
  /** @type {string} */
  #status
  /** @type {boolean} */
  #isAvailable
  /** @type {number} */
  #quantityAvailable
  /** @type {string} */
  #currencyCode
  /** @type {number} */
  #minValue
  /** @type {number} */
  #maxValue
  /** @type {number} */
  #faceValue
  /** @type {number} */
  #listPrice
  /** @type {ItemRewardType} */
  #rewardType
  /** @type {number} */
  #brandID
  /** @type {boolean} */
  #active
  /** @type {string} */
  #unitType
  /** @type {boolean} */
  #isWholeAmountValueRequired
  /** @type {ItemCostAdjustment} */
  #adjustment
  /** @type {string} */
  #exchangeRateRule
  /** @type {Date} */
  #createdDate
  /** @type {Date} */
  #lastUpdateDate
  /** @type {FulfillmentType} */
  #fulfillmentType

  constructor (jsonObject) {
    super()
    this.#itemID = Number.parseInt(jsonObject.itemID) || undefined
    this.#utid = jsonObject.utid
    this.#sku = jsonObject.sku
    this.#name = jsonObject.name
    this.#rewardName = jsonObject.rewardName
    this.#valueType = ItemRewardValueType.fromJSON(jsonObject.valueType)
    this.#status = jsonObject.status
    this.#isAvailable = Boolean(String(jsonObject.isAvailable).toLowerCase() === 'true')
    this.#quantityAvailable = Number.parseInt(jsonObject.quantityAvailable) || undefined
    this.#currencyCode = jsonObject.currencyCode
    this.#minValue = Number.parseFloat(jsonObject.minValue)
    this.#maxValue = Number.parseFloat(jsonObject.maxValue)
    this.#faceValue = Number.parseFloat(jsonObject.faceValue)
    this.#listPrice = Number.parseFloat(jsonObject.listPrice)
    this.#rewardType = ItemRewardType.fromJSON(jsonObject.rewardType)
    this.#brandID = Number.parseInt(jsonObject.brandID) || undefined
    this.#active = Boolean(String(jsonObject.active).toLowerCase() === 'true')
    this.#unitType = jsonObject.unitType
    this.#isWholeAmountValueRequired = Boolean(String(jsonObject.isWholeAmountValueRequired).toLowerCase() === 'true')
    this.#adjustment = ItemCostAdjustment.fromJSON(jsonObject.adjustment)
    this.#exchangeRateRule = jsonObject.exchangeRateRule
    this.#createdDate = new Date(jsonObject.createdDate || undefined)
    this.#lastUpdateDate = new Date(jsonObject.lastUpdateDate || undefined)
    this.#fulfillmentType = FulfillmentType.fromJSON(jsonObject.fulfillmentType)
    Object.freeze(this)
  }

  get itemID () {
    return this.#itemID
  }

  get utid () {
    return this.#utid
  }

  /**
   * @deprecated
   * @return {string}
   */
  get sku () {
    return this.#sku
  }

  get name () {
    return this.#name
  }

  get rewardName () {
    return this.#rewardName
  }

  get valueType () {
    return this.#valueType
  }

  /**
   * @deprecated
   * @return {string}
   */
  get status () {
    return this.#status
  }

  get isAvailable () {
    return this.#isAvailable
  }

  get quantityAvailable () {
    return this.#quantityAvailable
  }

  get currencyCode () {
    return this.#currencyCode
  }

  get minValue () {
    return this.#minValue
  }

  get maxValue () {
    return this.#maxValue
  }

  get faceValue () {
    return this.#faceValue
  }

  /**
   * @deprecated
   * @return {number}
   */
  get listPrice () {
    return this.#listPrice
  }

  get rewardType () {
    return this.#rewardType
  }

  get brandID () {
    return this.#brandID
  }

  /**
   * @deprecated
   * @return {boolean}
   */
  get active () {
    return this.#active
  }

  /**
   * @deprecated
   * @return {string}
   */
  get unitType () {
    return this.#unitType
  }

  get isWholeAmountValueRequired () {
    return this.#isWholeAmountValueRequired
  }

  get adjustment () {
    return this.#adjustment
  }

  /**
   * @deprecated
   * @return {string}
   */
  get exchangeRateRule () {
    return this.#exchangeRateRule
  }

  /**
   * @deprecated
   * @return {Date}
   */
  get createdDate () {
    return this.#createdDate
  }

  /**
   * @deprecated
   * @return {Date}
   */
  get lastUpdateDate () {
    return this.#lastUpdateDate
  }

  get fulfillmentType () {
    return this.#fulfillmentType
  }

  /**
   * @return {Object}
   */
  toJSON () {
    return {
      itemID: this.#itemID,
      utid: this.#utid,
      sku: this.#sku,
      name: this.#name,
      rewardName: this.#rewardName,
      valueType: this.#valueType?.toJSON(),
      status: this.#status,
      isAvailable: this.#isAvailable,
      quantityAvailable: this.#quantityAvailable,
      currencyCode: this.#currencyCode,
      minValue: this.#minValue,
      maxValue: this.#maxValue,
      faceValue: this.#faceValue,
      listPrice: this.#listPrice,
      rewardType: this.#rewardType?.toJSON(),
      brandID: this.#brandID,
      active: this.#active,
      unitType: this.#unitType,
      isWholeAmountValueRequired: this.#isWholeAmountValueRequired,
      adjustment: this.#adjustment?.toJSON(),
      exchangeRateRule: this.#exchangeRateRule,
      createdDate: this.#createdDate?.valueOf() || undefined,
      lastUpdateDate: this.#lastUpdateDate?.valueOf() || undefined,
      fulfillmentType: this.#fulfillmentType?.toJSON()
    }
  }

  /**
   * @param {Object} jsonObject
   * @return {BrandItemResponse|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new BrandItemResponse(jsonObject)) || undefined
  }
}
